import type { Locale } from "@/i18n/routing";
import type { LineOptionRow } from "@/lib/commerce/line-options";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * A sold line's variant and add-ons, one row per axis or group: its name, then
 * what was picked under it. The variant's rows come first and a rule (a gap
 * on paper) sets the add-ons apart; an unnamed row takes the caller's generic
 * label, so a variant and an add-on never read as one list.
 *
 * `chips` (screens) shows the picks as chips, filled for a variant and
 * outlined for an add-on. `text` (invoices) prints "Name: pick, pick", because
 * a printed page has no chips. Neither aligns names in a column: a merchant's
 * group name can be a sentence, and a column sized to it squeezes every value
 * beside it. Names and picks are `<bdi>`, so an Arabic name on an English page
 * keeps its colon on the right side.
 *
 * Hook-free, so a server page, a client cart and a print invoice share it; the
 * caller passes the two labels because the namespaces differ per surface.
 */
export function LineOptions({
	rows,
	locale,
	labels,
	display = "chips",
	className,
}: {
	rows: LineOptionRow[];
	locale: Locale;
	labels: { variant: string; addons: string };
	display?: "chips" | "text";
	className?: string;
}) {
	if (rows.length === 0) return null;
	// A plain separator, not `Intl.ListFormat`: its Arabic list puts «و» before
	// the last entry even as `unit`, and this is an enumeration, not a sentence.
	const separator = locale === "ar" ? "، " : ", ";
	const firstAddon = rows.findIndex((r) => r.kind === "addons");

	return (
		<dl className={cn("text-xs", display === "chips" && "space-y-1", className)}>
			{rows.map((row, i) => (
				<div
					// oxlint-disable-next-line react/no-array-index-key -- rows are derived per render and never reorder
					key={i}
					className={cn(
						display === "chips" && "flex flex-wrap items-center gap-x-2 gap-y-1",
						i === firstAddon &&
							i > 0 &&
							(display === "chips" ? "mt-1.5 border-t border-dashed pt-1.5" : "mt-1"),
					)}
				>
					<dt className={display === "chips" ? "text-muted-foreground" : "inline font-medium"}>
						<bdi>{row.name ?? (row.kind === "variant" ? labels.variant : labels.addons)}</bdi>
						{display === "text" && ": "}
					</dt>
					{display === "chips" ? (
						<dd className="flex flex-wrap gap-1">
							{row.values.map((value, j) => (
								<Badge
									// oxlint-disable-next-line react/no-array-index-key -- a line may pick the same label twice
									key={j}
									variant={row.kind === "variant" ? "secondary" : "outline"}
									className="h-auto min-h-5 text-start font-normal whitespace-normal"
								>
									{value}
								</Badge>
							))}
						</dd>
					) : (
						<dd className="inline">
							{row.values.map((value, j) => (
								// oxlint-disable-next-line react/no-array-index-key -- a line may pick the same label twice
								<span key={j}>
									{j > 0 && separator}
									<bdi>{value}</bdi>
								</span>
							))}
						</dd>
					)}
				</div>
			))}
		</dl>
	);
}
